import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ProjectImport, UpdateProjectImportRequest } from '../models/classic-import.model';
import { filenameFromContentDisposition } from '../shared/file-download';

export interface ReportDownload {
  blob: Blob;
  filename: string;
}

/** Classic-Import in ein Projekt: Upload mit Probelauf, Auswahl und Antworten, Bericht, Übernahme. */
@Injectable({ providedIn: 'root' })
export class ClassicImportService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/api/v1/projects`;

  upload(projectId: string, file: File): Observable<ProjectImport> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ProjectImport>(this.importsUrl(projectId), formData);
  }

  get(projectId: string, importId: string): Observable<ProjectImport> {
    return this.http.get<ProjectImport>(this.importUrl(projectId, importId));
  }

  update(projectId: string, importId: string, body: UpdateProjectImportRequest): Observable<ProjectImport> {
    return this.http.put<ProjectImport>(this.importUrl(projectId, importId), body);
  }

  downloadReport(projectId: string, importId: string, format: 'md' | 'json'): Observable<ReportDownload> {
    const params = new HttpParams().set('format', format);
    return this.http
      .get(`${this.importUrl(projectId, importId)}/report`, { params, responseType: 'blob', observe: 'response' })
      .pipe(
        map((response) => ({
          blob: response.body ?? new Blob(),
          filename: filenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            `import-bericht-${importId}.${format}`,
          ),
        })),
      );
  }

  commit(projectId: string, importId: string): Observable<ProjectImport> {
    return this.http.post<ProjectImport>(`${this.importUrl(projectId, importId)}/commit`, null);
  }

  /** Deletes the uploaded file of an import that was not committed; the import and its report stay. */
  discard(projectId: string, importId: string): Observable<void> {
    return this.http.delete<void>(this.importUrl(projectId, importId));
  }

  private importsUrl(projectId: string): string {
    return `${this.baseUrl}/${projectId}/imports`;
  }

  private importUrl(projectId: string, importId: string): string {
    return `${this.importsUrl(projectId)}/${importId}`;
  }
}
